/**
 * useFloorKeyboard — the desktop keyboard layer (web only; a no-op on
 * native, where the toolbar/touch equivalents cover every action).
 *
 * Two responsibilities:
 *
 * 1. SHORTCUTS — window keydown → `resolveShortcut` (Ctrl↔Cmd normalized)
 *    → editor ops. Standard Figma/Illustrator bindings only; handled keys
 *    are preventDefault'ed so the browser's Ctrl+D / Ctrl+A / Ctrl+± don't
 *    fire, unhandled keys pass through untouched.
 * 2. MODIFIER CHANNELS — mirrors Shift / Alt / Space into the editor's
 *    modifier shared values so pointer gestures read them live (axis lock,
 *    aspect lock, duplicate-drag, space-pan). Synced from EVERY key event's
 *    own flags + reset on window blur, so alt-tabbing can't strand a stuck
 *    modifier.
 *
 * Mutating actions are suppressed in VIEW mode (read-only staff); pure
 * navigation (zoom/fit) always works. Events originating in text inputs
 * are ignored entirely.
 *
 * @module floorplan/hooks/useFloorKeyboard
 * @category FloorPlan
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import type { ShortcutAction } from '../keyboard/shortcuts';
import { normalizeKeyEvent, resolveShortcut } from '../keyboard/shortcuts';
import { FLOOR_MODE, FLOOR_TOOL } from '../models/mode';
import { useFloorEngineStore } from '../stores/useFloorEngineStore';
import type useFloorEditor from './useFloorEditor';

/** Arrow-key nudge distances (world units) — Figma's 1 / 10 small/big. */
const NUDGE_STEP = 1;
const NUDGE_STEP_BIG = 10;

type FloorEditor = ReturnType<typeof useFloorEditor>;

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (target === null || typeof HTMLElement === 'undefined') return false;
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
};

/** Actions that mutate the document — blocked for read-only (VIEW) users. */
const MUTATING_ACTIONS: ReadonlySet<ShortcutAction> = new Set([
  'undo',
  'redo',
  'cut',
  'paste',
  'duplicate',
  'delete',
  'group',
  'ungroup',
  'bring-forward',
  'send-backward',
  'bring-front',
  'send-back',
  'nudge-left',
  'nudge-right',
  'nudge-up',
  'nudge-down',
  'nudge-left-big',
  'nudge-right-big',
  'nudge-up-big',
  'nudge-down-big',
  'tool-rect',
  'tool-ellipse',
  'tool-line',
  'tool-text',
  'tool-marquee',
]);

const dispatchAction = (action: ShortcutAction, editor: FloorEditor): void => {
  const store = useFloorEngineStore.getState();
  const readOnly = store.mode === FLOOR_MODE.VIEW;
  if (readOnly && MUTATING_ACTIONS.has(action)) return;

  switch (action) {
    case 'undo':
      editor.history.undo();
      return;
    case 'redo':
      editor.history.redo();
      return;
    case 'copy':
      editor.ops.copySelection();
      return;
    case 'cut':
      editor.ops.cutSelection();
      return;
    case 'paste':
      editor.ops.paste();
      return;
    case 'duplicate':
      editor.ops.duplicateSelection();
      return;
    case 'delete':
      editor.ops.deleteSelection();
      return;
    case 'group':
      editor.ops.groupSelection();
      return;
    case 'ungroup':
      editor.ops.ungroupSelection();
      return;
    case 'bring-forward':
      editor.ops.reorderSelection('forward');
      return;
    case 'send-backward':
      editor.ops.reorderSelection('backward');
      return;
    case 'bring-front':
      editor.ops.reorderSelection('front');
      return;
    case 'send-back':
      editor.ops.reorderSelection('back');
      return;
    case 'select-all':
      editor.ops.selectAll();
      return;
    case 'deselect-all':
      store.clearSelection();
      return;
    case 'escape':
      editor.ops.escape();
      return;
    case 'nudge-left':
      editor.ops.nudgeSelection(-NUDGE_STEP, 0);
      return;
    case 'nudge-right':
      editor.ops.nudgeSelection(NUDGE_STEP, 0);
      return;
    case 'nudge-up':
      editor.ops.nudgeSelection(0, -NUDGE_STEP);
      return;
    case 'nudge-down':
      editor.ops.nudgeSelection(0, NUDGE_STEP);
      return;
    case 'nudge-left-big':
      editor.ops.nudgeSelection(-NUDGE_STEP_BIG, 0);
      return;
    case 'nudge-right-big':
      editor.ops.nudgeSelection(NUDGE_STEP_BIG, 0);
      return;
    case 'nudge-up-big':
      editor.ops.nudgeSelection(0, -NUDGE_STEP_BIG);
      return;
    case 'nudge-down-big':
      editor.ops.nudgeSelection(0, NUDGE_STEP_BIG);
      return;
    case 'zoom-in':
      editor.ops.zoomBy(1.25);
      return;
    case 'zoom-out':
      editor.ops.zoomBy(0.8);
      return;
    case 'zoom-fit':
      editor.ops.fit();
      return;
    case 'zoom-100':
      editor.ops.zoomTo(1);
      return;
    case 'tool-move':
      store.setMode(FLOOR_MODE.EDIT);
      return;
    case 'tool-hand':
      store.setMode(FLOOR_MODE.PAN);
      return;
    case 'tool-marquee':
      store.setMode(FLOOR_MODE.SELECTION);
      return;
    case 'tool-rect':
      store.setMode(FLOOR_MODE.DRAW);
      store.setActiveTool(FLOOR_TOOL.RECTANGLE);
      return;
    case 'tool-ellipse':
      store.setMode(FLOOR_MODE.DRAW);
      store.setActiveTool(FLOOR_TOOL.CIRCLE);
      return;
    case 'tool-line':
      store.setMode(FLOOR_MODE.DRAW);
      store.setActiveTool(FLOOR_TOOL.LINE);
      return;
    case 'tool-text':
      store.setMode(FLOOR_MODE.DRAW);
      store.setActiveTool(FLOOR_TOOL.TEXT);
      return;
    default:
      return;
  }
};

/**
 * Attach the keyboard layer for the lifetime of the editor screen.
 * `editor` is captured through a ref so the listener is registered ONCE
 * (no add/remove churn per render — the editor object is a fresh literal
 * every render by design in this compiler-excluded tree).
 */
const useFloorKeyboard = (editor: FloorEditor): void => {
  const editorRef = useRef(editor);
  // Assigned post-commit (not during render) to satisfy react-hooks/refs.
  useEffect(() => {
    editorRef.current = editor;
  });

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return undefined;
    }

    const isMac = /mac/i.test(
      typeof navigator === 'undefined' ? '' : navigator.platform,
    );

    const syncModifiers = (event: KeyboardEvent) => {
      const live = editorRef.current;
      live.modifiers.shift.set(event.shiftKey ? 1 : 0);
      live.modifiers.alt.set(event.altKey ? 1 : 0);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      syncModifiers(event);
      if (event.key === ' ') {
        editorRef.current.modifiers.space.set(1);
        // Space would scroll the page; the canvas owns it as the hand tool.
        event.preventDefault();
        return;
      }
      const action = resolveShortcut(normalizeKeyEvent(event, isMac));
      if (action === null) return;
      event.preventDefault();
      dispatchAction(action, editorRef.current);
    };

    const onKeyUp = (event: KeyboardEvent) => {
      syncModifiers(event);
      if (event.key === ' ') editorRef.current.modifiers.space.set(0);
    };

    const onBlur = () => {
      const live = editorRef.current;
      live.modifiers.shift.set(0);
      live.modifiers.alt.set(0);
      live.modifiers.space.set(0);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
};

export default useFloorKeyboard;
